import React, { useMemo } from "react";
import styled from "styled-components";
import { Droppable } from "react-beautiful-dnd";
import { useTranslation } from "react-i18next";
import { Tooltip, Icon, Button } from "antd";
import { useSelector } from "react-redux";
import * as R from "ramda";
import { useDispatch } from "react-redux";

import * as gtag from "../../../../../lib/gtag";
import Element from "../../../../../components/structure/Element";
import api from "../../../../../utils/api";
import {
  hasEmptyRooms,
  hasEmptyMap,
  getChatbotStatus,
  isLastOfferPending,
  defaultMatchesFilters,
  getStepDisplayName
} from "../selectors";
import { DELETE_MESSAGE } from "../../../../../store/actions/chat";
import { DEFAULT_PROCESS_STEPS } from "../../../../../constants/opportunity";
import SearchFilter from "../../OpportunityCandidates/filters/SearchFilter";

import { getColumnWarning } from "./selectors";
import Match from "./Match";
import Filters from "./Filters";

const Container = styled.div`
  background: ${({ theme }) => theme["@grey"]};
  min-width: 300px;
  width: 300px;
  height: 100%;
  transition: opacity 0.25s;
  border-radius: 3px;
  opacity: ${({ isDisabled }) => (isDisabled ? 0.7 : 1)};
  position: relative;
  padding-top: 48px;
  display: flex;
  flex-direction: column;

  &:not(:last-child) {
    margin-right: 12px;
  }
`;

const Header = styled.div`
  color: ${({ theme }) => theme["@dark"]};
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: flex-start;
  min-height: 12px;
  background: ${({ theme }) => theme["@grey"]};
  margin: 0;
  padding: 12px;
  border-radius: 3px 3px 0 0;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  background: ${({ theme }) => theme["@grey"]};
  margin: 0;
  margin-top: 6px;
  padding-left: 6px;
  margin-left: 6px;
  padding-right: 9px;
  margin-right: 3px;
  border-radius: 0 0 3px 3px;
  height: 100%;
  ${({ isDisabled }) => (isDisabled ? "pointer-events: none;" : "")}

  @supports (-ms-ime-align: auto) {
    position: absolute;
    top: 48px;
    height: calc(100% - 48px);
  }

  &::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 4px;
    background-color: rgba(0, 0, 0, 0.3);
    box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
  }

  &::-webkit-scrollbar-track {
    background-color: rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  &:nth-child(2) {
    padding-top: 0;
  }
`;

const ColumnTitle = styled.h3`
  margin: 0;
  word-break: break-all;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Actions = styled.div`
  display: flex;
  margin-left: auto;
  white-space: no-wrap;
  div {
    display: flex;
  }
`;

const ActionIcon = styled(Icon)`
  font-size: 20px !important;
  cursor: pointer;
  transition: 0.25s all;
  will-change: opacity, transform;

  &:hover {
    opacity: 0.5;
    transform: scale(1.25);
  }

  &:not(:last-child) {
    margin-right: 6px;
  }
`;

const MatchEmpty = styled.div`
  border: 2px dashed rgba(0, 0, 0, 0.1);
  margin-bottom: 8px;
  width: 276px;
  height: 285px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PaginationFooter = styled.div`
  padding: 8px 0 12px;
  text-align: center;
`;

const MatchesCounter = styled.div`
  color: ${({ theme }) => theme["@text-color-secondary"]};
  font-size: 12px;
  text-align: center;
  width: 100%;
  margin: 12px 0 10px;
`;

const Step = ({
  step,
  lastStep,
  sendOffer,
  filters,
  setFilters,
  match,
  isDraggingFrom,
  canDropTo,
  openPositions,
  removeMatch,
  canMove,
  canChat,
  isLoading,
  updateStep,
  opportunity,
  selectCandidate,
  selectedCandidates,
  selectedCandidatesStep,
  stepPagination,
  loadedMatchesCount,
  totalMatchesCount,
  loadStepMatches,
  onEdit,
  ...props
}) => {
  const { t } = useTranslation(["opportunity"]);
  const company = useSelector(({ company }) => company);
  const dispatch = useDispatch();

  const areMatchesExpanded = step.mode === "full" || !step.mode;
  const warning = getColumnWarning(step);
  const isLastStepDisabled = openPositions - lastStep.matches.length <= 0;
  const isDisabled =
    (isDraggingFrom &&
      (!canDropTo ||
        !hasEmptyRooms(step) ||
        hasEmptyMap(step) ||
        isLastOfferPending(match))) ||
    Boolean(selectedCandidates.length && selectedCandidatesStep.id !== step.id);
  const hasKnownTotal = totalMatchesCount > 0;
  const nextPage = R.propOr(1, "page", stepPagination) + 1;

  function toggleMatchesMode() {
    const mode = areMatchesExpanded ? "compact" : "full";
    gtag.event({
      category: "Process",
      action: `Process.${!areMatchesExpanded ? "Expand" : "Collapse"}`,
      label: step.idOpportunity
    });

    api.opportunity.process.step.update(
      company.id,
      step.idOpportunity,
      step.id,
      { mode }
    );

    updateStep(step.id, { mode });
  }

  const chatbotTags = useMemo(() => R.propOr([], "chatbotTags", match), [
    match
  ]);

  return (
    <>
      <Container
        {...props}
        isDisabled={isDisabled}
        data-test={`step.${step.id}`}
        data-test-disabled-step={`${isDisabled}`}
      >
        <Header>
          <Filters
            step={step}
            filters={filters || defaultMatchesFilters}
            setFilters={setFilters}
          />
          {step.automaticStep && (
            <Tooltip title={t("opportunity:process.stepDetails.automaticStep")}>
              <Icon
                type="thunderbolt"
                style={{ color: "blue", marginRight: 5, marginLeft: -9 }}
              />
            </Tooltip>
          )}
          <ColumnTitle title={getStepDisplayName(step, t)}>
            {getStepDisplayName(step, t)}
          </ColumnTitle>
          <Actions>
            {warning && (
              <Element marginRight={6}>
                <Tooltip title={warning}>
                  <ActionIcon type="warning" style={{ color: "red" }} />
                </Tooltip>
              </Element>
            )}
            {step.name === DEFAULT_PROCESS_STEPS.accepted &&
              isLastStepDisabled && (
                <Element marginRight={6}>
                  <Tooltip
                    title={t("opportunity:process.stepDetails.fullPositions")}
                  >
                    <ActionIcon
                      data-test="full-opportunity-warning"
                      type="warning"
                      style={{ color: "red" }}
                    />
                  </Tooltip>
                </Element>
              )}
            <Element marginRight={6}>
              <Tooltip
                title={t(
                  areMatchesExpanded
                    ? "opportunity:process.stepDetails.shrinkMatches"
                    : "opportunity:process.stepDetails.expandMatches"
                )}
              >
                <ActionIcon
                  onClick={toggleMatchesMode}
                  type={areMatchesExpanded ? "shrink" : "arrows-alt"}
                  data-test={
                    areMatchesExpanded
                      ? "expanded-matches-icon"
                      : "shrinked-matches-icon"
                  }
                />
              </Tooltip>
            </Element>
            {(!step.default ||
              step.name === DEFAULT_PROCESS_STEPS.selected ||
              step.name === DEFAULT_PROCESS_STEPS.accepted) && (
              <div data-test="open-step-modal-btn">
                <ActionIcon onClick={() => onEdit()} type="setting" />
              </div>
            )}
          </Actions>
        </Header>
        <SearchFilter
          searchText={(filters || defaultMatchesFilters).searchText}
          setSearchText={value =>
            setFilters(
              R.assoc("searchText", value, filters || defaultMatchesFilters)
            )
          }
        />
        <MatchesCounter>
          {hasKnownTotal
            ? t("opportunity:process.stepDetails.matchesCounter", {
                shown: loadedMatchesCount,
                total: totalMatchesCount
              })
            : t("opportunity:process.stepDetails.matchesShown", {
                shown: loadedMatchesCount
              })}
        </MatchesCounter>
        <Droppable
          key={step.id}
          droppableId={step.id}
          isDropDisabled={isDisabled}
        >
          {provided => (
            <Body
              isDisabled={isDisabled}
              ref={provided.innerRef}
              {...provided.droppableProps}
            >
              {R.addIndex(R.map)((match, index) => {
                return (
                  <Match
                    stepIsDisabled={isDisabled}
                    selectCandidate={selectCandidate}
                    selectedCandidates={selectedCandidates}
                    key={match.id}
                    mode={step.mode}
                    opportunity={opportunity}
                    chatbotStatus={getChatbotStatus(step, match)}
                    chatbotTags={chatbotTags}
                    id={match.id}
                    isLoading={isLoading}
                    index={index}
                    data={match}
                    step={step}
                    sendOffer={sendOffer}
                    canMove={canMove}
                    canChat={canChat}
                    onRemove={() => {
                      removeMatch(step.id, match.id);
                      // TODO change propOr to prop
                      const isRegistered = R.propOr(true, "isRegistered");
                      if (isRegistered) {
                        dispatch(
                          DELETE_MESSAGE.run({
                            idOpportunity: opportunity.id,
                            idCandidate: match.id
                          })
                        );
                      }
                    }}
                  />
                );
              })(step.matches)}
              {step.id === lastStep.id &&
                openPositions - step.matches.length > 0 &&
                Array(openPositions - step.matches.length)
                  .fill("")
                  .map((_, index) => (
                    <MatchEmpty key={index}>
                      <h4>{t("opportunity:process.stepDetails.empty")}</h4>
                    </MatchEmpty>
                  ))}
              {provided.placeholder}
              <PaginationFooter>
                {R.prop("hasNext", stepPagination) && (
                  <Button
                    block
                    loading={R.prop("loading", stepPagination)}
                    onClick={() => loadStepMatches(step.id, nextPage)}
                    data-test={`step.${step.id}.load-more`}
                  >
                    {t("opportunity:process.stepDetails.loadMore")}
                  </Button>
                )}
              </PaginationFooter>
            </Body>
          )}
        </Droppable>
      </Container>
    </>
  );
};

export default Step;
